import { useClickOutside } from '@mnd-frontend/hooks';
import {
  Badge,
  GREY_100,
  GREY_200,
  GREY_400,
  GREY_1000,
  Icons,
  NEUTRAL_WHITE,
  RED_700,
  SHADOW_1,
  SPACING_0_5,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
  TEXT_FONT,
  media,
} from '@mnd-frontend/ui';
import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { matchPath, useLocation } from 'react-router';
import styled, { css } from 'styled-components';
import { track } from '../../../../lib/tracker';
import NavigationLink from './NavigationLink';
import { MenuItem } from './common';
import type { Item, ItemGroup, ItemGroupWithHeading, ItemGroupWithSeparator } from './types';

const Button = styled.button`
  border: 0;
  background: transparent;
  height: 100%;
  text-align: left;
  cursor: pointer;
  padding: 1px 6px;
`;

const SubMenuItem = styled.li`
  margin: 0;
  display: inline-block;

  a {
    display: flex;
    height: 100%;
    align-items: center;
    color: ${GREY_1000};
    padding: 1.125rem ${SPACING_1_5};
    &.nav-link-active {
      color: ${RED_700};
    }
    text-decoration: none;
  }
`;

const baseMenuCss = css`
  position: absolute;
  margin: 0;
  margin-top: ${SPACING_1};
  box-shadow: ${SHADOW_1};
  min-width: 16rem;

  ${media.lessThan('phablet')} {
    left: 0;
    width: 100%;
    min-width: unset;
  }
`;

const DropdownMenuBase = styled.div`
  ${baseMenuCss}
  left: 2rem;
  background: ${GREY_200};
  border-radius: 0.5rem;
  ${media.lessThan('phablet')} {
    left: 0;
    width: 100%;
    min-width: unset;
  }
`;

export const DropdownMenu = styled.div`
  ${baseMenuCss}
  background: ${NEUTRAL_WHITE};
  border-radius: 0.25rem;
  border: 1px solid ${GREY_400};
  padding: ${SPACING_1};
  ul {
    list-style: none;
    padding: 0;
  }
`;

const DropdownHeading = styled.h3`
  color: ${GREY_1000};
  font-weight: 600;
  font-size: 0.75rem;
  font-family: ${TEXT_FONT};

  line-height: 1.125rem;
  margin: ${SPACING_2} ${SPACING_1} ${SPACING_1} ${SPACING_2};
  text-transform: uppercase;
`;

const SeparatorItem = styled.li`
  margin: 0;
  padding: ${SPACING_0_5} 0;
  hr {
    border: 0;
    background: ${GREY_400};
    margin: 0;
    height: 1px;
  }
`;
const DropdownMenuItem = styled.li`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.25;
  border-radius: 0.25rem;
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${SPACING_1};
    color: ${GREY_1000};
    &:active,
    &:hover,
    &:focus {
      color: ${GREY_1000};
      text-decoration: none;
    }

    &.nav-link-active {
      font-weight: bold;
      color: ${GREY_1000};
      background: ${GREY_200};
    }
  }
  &:hover {
    background: ${GREY_100};
  }
`;

const SubMenu = styled.div`
  border-top: 1px solid ${GREY_400};
  width: 100%;
`;
export const BadgeWrapper = styled.div`
  margin-left: ${SPACING_1};
  display: inline-block;
  line-height: 0;
  > span {
    position: static;
  }
`;

const SubMenuInner = styled.ul`
  margin: 0 auto;
  width: 100%;
  max-width: var(--page-width);
  min-height: 3.5rem;
  grid-gap: ${SPACING_1_5};
  list-style: none;
  padding: 0;
`;

const RedDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  background: ${RED_700};
  border-radius: 50%;
`;
interface Props {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  itemGroups: ItemGroup[] | ItemGroupWithSeparator[] | ItemGroupWithHeading[];
  children: React.ReactNode;
  menuContent?: React.ReactNode;
  disableOutsideClick?: boolean;
  subMenuPortalNode?: HTMLDivElement | null;
  className?: string;
  'data-testid'?: string;
}

const isItemGroupWithHeading = (
  item: ItemGroupWithHeading | ItemGroupWithSeparator | ItemGroup,
): item is ItemGroupWithHeading => 'heading' in item;
const isItemGroupWithSeparator = (
  item: ItemGroupWithHeading | ItemGroupWithSeparator | ItemGroup,
): item is ItemGroupWithSeparator => 'separator' in item;
const MenuButton = ({
  subMenuPortalNode,
  isOpen,
  onOpen,
  onClose,
  itemGroups,
  menuContent,
  children,
  className,
  'data-testid': dataTestId,
  disableOutsideClick = false,
}: Props) => {
  const ref = useRef<HTMLLIElement>(null);
  useClickOutside(
    ref,
    () => {
      if (isOpen && !disableOutsideClick) {
        onClose();
      }
    },
    isOpen,
  );

  const flatItems: Item[] = [];
  const matchUrls: string[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const item of itemGroups) {
    // eslint-disable-next-line no-restricted-syntax
    for (const innerItem of item.items) {
      flatItems.push(innerItem);
      if (Array.isArray(innerItem.activeUrl)) {
        matchUrls.push(...innerItem.activeUrl);
      } else {
        matchUrls.push(innerItem.activeUrl || innerItem.url);
      }
    }
  }
  const location = useLocation();

  const match = matchUrls.find(url => matchPath(url, location.pathname));
  if (flatItems.length === 0 && !menuContent) {
    return null;
  }
  const filteredGroups = itemGroups.filter(group => group.items.length > 0);

  return (
    <>
      <MenuItem ref={ref} $isActive={!!match} className={className}>
        <Button
          data-testid={dataTestId}
          onClick={() => {
            if (isOpen) {
              onClose();
            } else {
              onOpen();
            }
          }}
        >
          {children}
        </Button>
        {isOpen &&
          (menuContent ? (
            <DropdownMenuBase>{menuContent}</DropdownMenuBase>
          ) : (
            <DropdownMenu data-testid={`${dataTestId}_submenu`}>
              {filteredGroups.map((itemGroup, index) => (
                <React.Fragment key={index}>
                  {isItemGroupWithHeading(itemGroup) && (
                    <DropdownHeading>{itemGroup.heading}</DropdownHeading>
                  )}
                  <ul>
                    {itemGroup.items.map(item => (
                      <DropdownMenuItem key={item.url}>
                        <NavigationLink
                          href={item.url}
                          onClick={() => {
                            onClose();
                            track({
                              event: 'Dropdown Menu Navigation - Clicked',
                              traits: {
                                navigated_to: String(item.text),
                              },
                            });
                          }}
                          newTab={item.newTab}
                          activeUrl={item.activeUrl || item.url}
                          exactMatch={item.exactMatch}
                        >
                          {item.text} {item.highlight && <RedDot />}
                          {item.badge && (
                            <BadgeWrapper>
                              <Badge variant={item.badge.variant} text={item.badge.text} />
                            </BadgeWrapper>
                          )}
                          {item.newTab && <Icons.ExternalLink $size="xxs" />}
                        </NavigationLink>
                      </DropdownMenuItem>
                    ))}
                    {filteredGroups.length - 1 !== index && isItemGroupWithSeparator(itemGroup) && (
                      <SeparatorItem>
                        <hr />
                      </SeparatorItem>
                    )}
                  </ul>
                </React.Fragment>
              ))}
            </DropdownMenu>
          ))}
      </MenuItem>
      {match &&
        subMenuPortalNode &&
        ReactDOM.createPortal(
          <SubMenu>
            <SubMenuInner>
              {flatItems.map(item => (
                <SubMenuItem key={item.url}>
                  <NavigationLink
                    href={item.url}
                    activeUrl={item.activeUrl || item.url}
                    exactMatch={item.exactMatch}
                    onClick={() =>
                      track({
                        event: 'Sub Menu Navigation - Clicked',
                        traits: {
                          navigated_to: String(item.text),
                        },
                      })
                    }
                  >
                    {item.text}
                    {item.badge && (
                      <BadgeWrapper>
                        <Badge variant={item.badge.variant} text={item.badge.text} />
                      </BadgeWrapper>
                    )}
                  </NavigationLink>
                </SubMenuItem>
              ))}
            </SubMenuInner>
          </SubMenu>,
          subMenuPortalNode,
        )}
    </>
  );
};

export default MenuButton;
